import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { loginInitialState } from "./login.types";
import { initialState } from "./login.initial.state";
import { normalizeValue } from "../../../utils/helpers";
import { checkIfEmailExists, verifyLogin } from "./login.actions";
import { getBoolParameterByName } from "../../../utils/urlParser";
import { AlertI } from "../../settings/common.types";

const initState: loginInitialState = initialState;

export const loginSlice = createSlice({
	name: "login",
	initialState: initState,
	reducers: {
		triggerAlert: (state, action: PayloadAction<AlertI[]>) => {
			state.alerts.push(...action.payload);
		},
		onAlertClose: (state, action: PayloadAction<AlertI[]>) => {
			state.alerts = action.payload;
		},
		handleFieldValueChange: (
			state,
			action: PayloadAction<{ field: string; value: any }>,
		) => {
			state.form[action.payload.field] = normalizeValue(action.payload.value);
		},
		handleQueryParamAlerts: (state) => {
			const config = {
				isForgotPasswordRedirect: {
					hasParam: getBoolParameterByName("isForgotPasswordRedirect"),
					messageKey: "komid.forgotpassword.success",
					level: "success",
				},
				isPasswordlessLoginExpiredRedirect: {
					hasParam: getBoolParameterByName(
						"isPasswordlessLoginExpiredRedirect",
					),
					messageKey: "komid.login.passwordlesslogin.successmessage",
					level: "success",
				},
				isExternalLoginCancelled: {
					hasParam: getBoolParameterByName("isExternalLoginCancelled"),
					messageKey: "komid.login.vipps.cancelled",
					level: "info",
				},
				isExternalLoginFailure: {
					hasParam: getBoolParameterByName("isExternalLoginFailure"),
					messageKey: "komid.login.vipps.failure",
					level: "error",
				},
				isAccountBlockedAwaitingApproval: {
					hasParam: getBoolParameterByName("isAccountBlockedAwaitingApproval"),
					messageKey:
						"komid.login.login-failed.accountblockedwaitingforapproval",
					level: "info",
				},
				isTwoFactorTimeout: {
					hasParam: getBoolParameterByName("isTwoFactorTimeout"),
					messageKey: "komid.login.twofactor.timeout",
					level: "info",
				},
				isMobileNumberMissing: {
					hasParam: getBoolParameterByName("isMobileNumberMissing"),
					messageKey: "komid.login.smslogin.mobileNumberMissing",
					level: "info",
				},
			};

			const alerts = Object.values(config)
				.map(({ hasParam, messageKey, level }) => {
					if (hasParam) {
						return {
							messageKey,
							level,
						};
					}
				})
				.filter(Boolean);

			state.alerts.push(...alerts);
		},
	},
	extraReducers(builder) {
		builder.addCase(checkIfEmailExists.pending, (state) => {
			state.isFetching = true;
			state.usernameExists = false;
			state.alerts = [];
		});

		builder.addCase(
			checkIfEmailExists.fulfilled,
			(
				state,
				action: PayloadAction<{
					isSuccess?: boolean;
					data?: { exists: boolean };
					error?: string;
				}>,
			) => {
				state.isFetching = false;
				state.usernameExists = action.payload.data.exists;

				if (action.payload.isSuccess) {
					if (action.payload.data.exists) {
						state.usernameExists = action.payload.data.exists;
					} else {
					}
				} else {
					state.alerts.push({
						level: "error",
						messageKey: action.payload.error,
					});
				}
			},
		);

		builder.addCase(checkIfEmailExists.rejected, (state) => {
			state.isFetching = false;
			state.alerts.push({
				level: "error",
				messageKey: "komid.general.network.error",
			});
		});

		builder.addCase(verifyLogin.pending, (state) => {
			state.isFetching = true;
			state.alerts = [];
		});

		builder.addCase(
			verifyLogin.fulfilled,
			(
				state,
				action: PayloadAction<{
					isSuccess?: boolean;
					isWrongPassword?: boolean;
					errorCode?: string;
					errorLevel?: string;
					errorMessage?: string;
					messageValues?: {
						[key: string]: string;
					};
					error?: string;
				}>,
			) => {
				state.isFetching = false;

				if (!action.payload.isSuccess && !action.payload.isWrongPassword) {
					state.alerts.push({
						level: action.payload.errorLevel,
						messageKey: action.payload.error,
						messageValues: action.payload.messageValues,
					});
				}
			},
		);

		builder.addCase(verifyLogin.rejected, (state) => {
			state.isFetching = false;
			state.alerts.push({
				level: "error",
				messageKey: "komid.general.network.error",
			});
		});
	},
});

export const {
	triggerAlert,
	onAlertClose,
	handleFieldValueChange,
	handleQueryParamAlerts,
} = loginSlice.actions;

export default loginSlice.reducer;
